import {
  isCollectorPaymentType,
  isValidRedirectUrl,
  PaymentType,
  REDIRECT_URL_MAX_LENGTH,
} from "Components/Paypoints/paymentType";
import { CurrencyCode, PayPointType } from "Infrastructure/Api/Api";
import { Resources, type TFunction } from "Translations/Resources";
import { z } from "zod";

export const getNfcCardNoSchema = (t: TFunction) =>
  z
    .string()
    .transform((value) => value || null)
    .nullish()
    .refine(
      (value) => {
        if (!value) return true; // Allow null/undefined
        //NFC card Code format: XX-XX-XX-XX-XX-XX-XX (14 characters with 6 hyphens)
        const cardCodeRegex =
          /^[0-9A-Z]{2}-[0-9A-Z]{2}-[0-9A-Z]{2}-[0-9A-Z]{2}-[0-9A-Z]{2}-[0-9A-Z]{2}-[0-9A-Z]{2}$/i;
        return cardCodeRegex.test(value);
      },
      {
        error: t(Resources.Paypoint.Form.NfcCardNoFormatError),
      },
    );

export const getPaypointSchema = (
  t: TFunction,
  isBankAccountRequired: boolean,
) =>
  z
    .object({
      publicID: z.string().nullish(),
      name: z
        .string()
        .min(1)
        .max(100, {
          message: t(Resources.Paypoint.Form.NameMaxLengthError, { max: 100 }),
        }),
      description: z.string().max(500).nullish(),
      type: z.enum(PayPointType),
      paymentType: z.enum(PaymentType),
      fixedAmount: z.number().positive().nullish(),
      currencyCode: z.enum(CurrencyCode).nullish(),
      tipsEnabled: z.boolean(),
      redirectUrl: z.string().max(REDIRECT_URL_MAX_LENGTH).nullish(),
      nfcCardNo: getNfcCardNoSchema(t),
      bankAccountPublicID: isBankAccountRequired
        ? z.string()
        : z.string().nullish(),
      facilityPublicID: z.string().nullish(),
      employeePublicIDs: z.array(z.string()).nullish(),
    })
    .refine(
      (data) =>
        data.paymentType !== "FixedPrice" ||
        (data.fixedAmount != null && data.fixedAmount > 0),
      {
        path: ["fixedAmount"],
        error: t(Resources.Paypoint.Form.FixedAmountRequired),
      },
    )
    // Mirrors the BE rule: every Collector paypoint carries a currency.
    .refine(
      (data) =>
        !isCollectorPaymentType(data.paymentType) || data.currencyCode != null,
      {
        path: ["currencyCode"],
        error: t(Resources.Paypoint.Form.CurrencyRequired),
      },
    )
    .refine(
      (data) =>
        data.paymentType !== "NfcLink" || isValidRedirectUrl(data.redirectUrl),
      {
        path: ["redirectUrl"],
        error: t(Resources.Paypoint.Form.RedirectUrlInvalid),
      },
    );

export type PaypointFormData = z.infer<ReturnType<typeof getPaypointSchema>>;
